/**
 * StageEditRow — FTP's grey name and stage type row for the selected stage
 * (admin-stage-details.php): the name input shows revert and save once it
 * changes (25 characters at most, required; their space is always kept, so
 * the stage type doesn't move when they appear), and the stage type select offers
 * Custom, the stage's own type and every global type no other stage uses
 * (deleted stages keep theirs). Inquiry's type is Inquiry alone, locked.
 *
 * The selected stage's delete sits at the end of the row: greyed out, naming
 * what blocks it, while automations point at the stage. Inquiry has none.
 */
import { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import {
  CUSTOM_STAGE_TYPE,
  STAGE_NAME_MAX,
  type PipelineStage,
  type StageTypeOption,
} from '@/services/stagePipelineAdmin.service';
import { STAGE_PIPELINE_BAR_CLASS } from './PipelineBar';

export interface StageEditRowProps {
  stage: PipelineStage;
  /** The free global types (page state). */
  stageTypes: StageTypeOption[];
  onSaveName: (name: string) => void;
  onChangeType: (typeId: number) => void;
  /** Why the stage can't be deleted; null when it can. */
  deleteBlockedReason: string | null;
  /** The blockers are still loading. */
  deleteChecking: boolean;
  onDelete: () => void;
}

/**
 * Why a name can't be saved, or null. Checked only once it changes (KTD10).
 * No 4-byte characters (emoji): the column is utf8mb3, and the API refuses them.
 */
export function stageNameError(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return 'A stage name is required';
  if (trimmed.length > STAGE_NAME_MAX) return `At most ${STAGE_NAME_MAX} characters`;
  if (/[\u{10000}-\u{10FFFF}]/u.test(trimmed)) return 'Emoji are not allowed';
  return null;
}

export function stageTypeOptions(stage: PipelineStage, free: StageTypeOption[]): { value: number; label: string }[] {
  if (stage.isInquiry) return [{ value: stage.typeId, label: stage.typeName || 'Inquiry' }];
  const own = stage.typeId !== CUSTOM_STAGE_TYPE && !free.some((t) => t.id === stage.typeId) ? [{ value: stage.typeId, label: stage.typeName }] : [];
  return [{ value: CUSTOM_STAGE_TYPE, label: 'Custom' }, ...own, ...free.map((t) => ({ value: t.id, label: t.name }))];
}

export function StageEditRow({
  stage,
  stageTypes,
  onSaveName,
  onChangeType,
  deleteBlockedReason,
  deleteChecking,
  onDelete,
}: StageEditRowProps) {
  const [draft, setDraft] = useState(stage.name);
  // A save (or a refresh) brings a new saved name: the field follows it.
  useEffect(() => setDraft(stage.name), [stage.name]);

  const dirty = draft !== stage.name;
  const error = dirty ? stageNameError(draft) : null;

  function save() {
    if (!dirty || error) return;
    onSaveName(draft.trim());
  }

  return (
    <div data-testid="stage-edit-row" className={cn(STAGE_PIPELINE_BAR_CLASS, 'gap-x-3')}>
      <form
        className="flex max-w-full flex-wrap items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Input
          aria-label="Stage name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-invalid={Boolean(error)}
          // Narrower on a phone, so revert and Save (44px touch targets there) fit beside it at 390px.
          className={cn('h-8 w-56 bg-white py-1 max-md:w-44', error && 'border-red-400 ring-1 ring-red-200')}
          wrapperClassName="w-auto"
          fullWidth={false}
        />
        {/*
          Always laid out, shown only once the name changes: the space stays
          reserved, so the stage type beside it never jumps (user, 2026-10-14).
          visibility: hidden also takes them out of the tab order and away from
          screen readers while they are not shown.
        */}
        <div
          data-testid="stage-name-actions"
          className="flex items-center gap-1"
          style={dirty ? undefined : { visibility: 'hidden' }}
          aria-hidden={dirty ? undefined : true}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Revert stage name"
            title="Revert"
            leftIcon={<RotateCcw className="h-4 w-4" />}
            disabled={!dirty}
            onClick={() => setDraft(stage.name)}
          />
          <Button type="submit" size="sm" aria-label="Save stage name" leftIcon={<Save className="h-4 w-4" />} disabled={!dirty || Boolean(error)}>
            Save
          </Button>
        </div>
      </form>
      {error && (
        <span role="alert" className="text-xs font-medium text-red-500">
          {error}
        </span>
      )}

      <div className="flex items-center gap-2">
        <label htmlFor="stage-pipeline-stage-type" className="text-slate-700">
          stage type
        </label>
        <Select<number>
          id="stage-pipeline-stage-type"
          options={stageTypeOptions(stage, stageTypes)}
          value={stage.typeId}
          disabled={stage.isInquiry}
          onChange={(v) => {
            if (v !== null && v !== undefined && v !== stage.typeId) onChangeType(v);
          }}
          wrapperClassName="w-48"
          className="h-8 bg-white py-1"
        />
      </div>

      {!stage.isInquiry && (
        <div className="ml-auto">
          <AdminRowAction
            kind="delete"
            label={`Delete ${stage.name}`}
            title={deleteChecking ? 'Checking what uses this stage…' : undefined}
            disabled={deleteChecking}
            blockedReason={deleteBlockedReason}
            onClick={onDelete}
          />
        </div>
      )}
    </div>
  );
}
